if (!customElements.get('wallify-frame-preview')) {
  const galleryFilters = new WeakMap();

  function frameGalleryFilter(gallery) {
    if (!gallery) return null;
    if (galleryFilters.has(gallery)) return galleryFilters.get(gallery);

    const root = gallery.closest('.product');
    if (!root) return null;
    const entries = new Map();
    let pending = null;
    let syncing = false;
    let disposed = false;
    let previousFramed = null;
    let previousPoster = null;
    let posterSelectionRequested = false;
    const slides = list => Array.from(list?.children || []).filter(node => node.classList.contains('splide__slide'));
    const dynamic = slide => !!slide.querySelector('wallify-set-frame-preview, wallify-frame-preview:not([data-lens-clone])');
    const selectedPosition = group => {
      const select = group?.querySelector('select');
      return select ? select.selectedIndex : Array.from(group?.querySelectorAll('input[type="radio"]') || []).findIndex(input => input.checked);
    };
    const schedule = () => {
      if (pending !== null || disposed) return;
      pending = requestAnimationFrame(() => { pending = null; sync(); });
    };
    const sync = () => {
      if (syncing || disposed || !gallery.isConnected) return;
      const mainList = gallery.main?.Components?.Elements?.list;
      const thumbList = gallery.thumbnails?.Components?.Elements?.list;
      const picker = root.querySelector('variant-selects');
      if (!mainList || !thumbList || !picker) return;
      const groups = Array.from(picker.children).filter(node => node.classList.contains('product-form__input'));
      // The catalog's first material is unframed; a stale color must not expose a frame.
      const framed = selectedPosition(groups[0]) > 0 && selectedPosition(groups[2]) > 0;
      const poster = selectedPosition(groups[0]) === 0;
      const materialBecameFramed = framed && previousFramed === false;
      const materialBecamePoster = poster && previousPoster === false && posterSelectionRequested;
      previousFramed = framed;
      previousPoster = poster;
      posterSelectionRequested = false;
      const originals = gallery.originalSlides?.length ? gallery.originalSlides : slides(mainList);
      const originalThumbs = gallery.originalThumbnails?.length ? gallery.originalThumbnails : slides(thumbList);
      originals.forEach((slide, index) => {
        const isDynamic = dynamic(slide);
        const isPoster = !isDynamic && slide.dataset.wallifyPosterPreview === 'true';
        if ((!isDynamic && !isPoster) || entries.has(slide.dataset.mediaId)) return;
        entries.set(slide.dataset.mediaId, {
          slide, index, poster: isPoster,
          thumb: originalThumbs.find(node => node.dataset.mediaId === slide.dataset.mediaId),
          unframedId: slide.querySelector('wallify-frame-preview[data-unframed-media-id]')?.dataset.unframedMediaId ||
            slide.querySelector('wallify-set-frame-preview')?.dataset.firstMedia
        });
      });
      if (!entries.size) return;
      const active = gallery.main.Components.Elements.slides[gallery.main.index] || mainList.querySelector('.is-active');
      let mediaId = active?.dataset.mediaId;
      let changed = false;
      syncing = true;
      try {
        entries.forEach(entry => {
          const attached = entry.slide.parentElement === mainList;
          const designAvailable = entry.slide.dataset.wallifyDesignAvailable !== 'false';
          if ((entry.poster ? !poster : !framed) || !designAvailable) {
            if (attached) {
              if (mediaId === entry.slide.dataset.mediaId) mediaId = entry.poster || !designAvailable ? entry.slide.dataset.wallifyDesignFallbackMediaId || entry.unframedId : entry.unframedId;
              entry.slide.remove(); changed = true;
            }
            if (entry.thumb?.parentElement === thumbList) { entry.thumb.remove(); changed = true; }
          } else {
            if (!attached) {
              const next = originals.slice(entry.index + 1).find(node => node.parentElement === mainList);
              mainList.insertBefore(entry.slide, next || null); changed = true;
              // A newly reattached frame preview follows the user's material selection.
              if (entry.poster ? materialBecamePoster : !entry.slide.hasAttribute('data-wallify-design-available') || materialBecameFramed) mediaId = entry.slide.dataset.mediaId;
            }
            if (entry.thumb && entry.thumb.parentElement !== thumbList) {
              const index = originalThumbs.indexOf(entry.thumb);
              const next = originalThumbs.slice(index + 1).find(node => node.parentElement === thumbList);
              thumbList.insertBefore(entry.thumb, next || null); changed = true;
            }
          }
        });
        if (!changed) return;
        gallery.main.refresh();
        gallery.thumbnails.refresh();
        const visible = gallery.main.Components.Elements.slides;
        let index = visible.findIndex(node => node.dataset.mediaId === mediaId);
        if (index < 0) index = 0;
        if (visible.length) { gallery.main.go(index); gallery.thumbnails.go(index); }
        gallery.updateSlideCounter?.(visible.length ? index : -1);
        gallery.updateThumbnailARIA?.();
      } finally { syncing = false; }
    };
    const handleChange = event => {
      const picker = event.target.closest?.('variant-selects');
      if (picker?.closest('.product') !== root) return;
      const material = Array.from(picker.children).filter(node => node.classList.contains('product-form__input'))[0];
      if (material?.contains(event.target)) posterSelectionRequested = selectedPosition(material) === 0;
      schedule();
    };
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    root.addEventListener('change', handleChange, true);
    root.addEventListener('wallify:premium-change', schedule, true);
    const handleDesignChange = () => { sync(); schedule(); };
    root.addEventListener('wallify:design-preview-change', handleDesignChange, true);
    const controller = {
      gallery, sync,
      dispose() {
        disposed = true; observer.disconnect();
        root.removeEventListener('change', handleChange, true);
        root.removeEventListener('wallify:premium-change', schedule, true);
        root.removeEventListener('wallify:design-preview-change', handleDesignChange, true);
        if (pending !== null) cancelAnimationFrame(pending);
        galleryFilters.delete(gallery);
      }
    };
    galleryFilters.set(gallery, controller);
    customElements.whenDefined('gallery-carousel').then(schedule);
    schedule();
    return controller;
  }

  class WallifyFramePreview extends HTMLElement {
    static preloadedSources = new Set();

    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
      this.pendingFrame = null;
    }

    connectedCallback() {
      if (this.hasAttribute('data-lens-clone')) return;
      this.productRoot = this.closest('.product');
      this.galleryFilter = frameGalleryFilter(this.closest('gallery-carousel'));
      this.productRoot?.addEventListener('change', this.handleChange, true);

      queueMicrotask(() => {
        if (!this.isConnected) return;

        const preserveInitialMedia =
          this.closest('gallery-carousel')?.dataset.wallifyPreserveInitialMedia === 'true' || this.closest('[data-wallify-design-available]') !== null;

        this.updateFrame({ updateGallery: !preserveInitialMedia });

        ['srcWhite', 'srcNaturalWood', 'srcDarkWood', 'srcBlack'].forEach((key) => {
          const source = this.dataset[key];

          if (!source || WallifyFramePreview.preloadedSources.has(source)) return;

          WallifyFramePreview.preloadedSources.add(source);

          const image = new Image();
          image.src = source;
        });
      });
    }

    disconnectedCallback() {
      this.productRoot?.removeEventListener('change', this.handleChange, true);
      this.productRoot = null;
      if (this.galleryFilter && !this.galleryFilter.gallery.isConnected) this.galleryFilter.dispose();
      if (this.pendingFrame !== null) cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }

    handleChange(event) {
      const picker = event.target.closest?.('variant-selects');

      if (!picker || picker.closest('.product') !== this.productRoot) return;
      if (this.pendingFrame !== null) return;

      this.pendingFrame = requestAnimationFrame(() => {
        this.pendingFrame = null;
        if (this.isConnected) this.updateFrame();
      });
    }

    updateFrame({ updateGallery = true } = {}) {
      const picker = this.productRoot?.querySelector('variant-selects');

      if (!picker) return;

      const containers = Array.from(picker.children).filter((child) =>
        child.classList.contains('product-form__input')
      );

      const container = containers[Number(this.dataset.frameOption)];

      if (!container) return;

      const select = container.querySelector('select');
      const radios = Array.from(
        container.querySelectorAll('input[type="radio"]')
      );

      const position = select
        ? select.selectedIndex
        : radios.findIndex((radio) => radio.checked);

      const key = [
        'none',
        'white',
        'natural-wood',
        'dark-wood',
        'black'
      ][position];

      if (!key) return;

      if (key === 'none') {
        this.dataset.frame = 'none';
        this.galleryFilter?.sync();
        if (updateGallery) this.updateCarousel(picker, key);
        return;
      }

      this.dataset.frame = key;
      this.galleryFilter?.sync();

      const selected = select
        ? select.selectedOptions[0]
        : radios[position];

      const label =
        selected?.dataset.wallifyDisplayValue ||
        selected?.value ||
        '';

      this.setAttribute(
        'aria-label',
        [this.dataset.productTitle, label].filter(Boolean).join(' — ')
      );

      if (updateGallery) this.updateCarousel(picker, key);
    }

    updateCarousel(picker, key) {
      if (
        this.classList.contains(
          'wallify-frame-preview--thumbnail'
        )
      ) {
        return;
      }

      const gallery = this.closest('gallery-carousel');
      const ownMediaId =
        this.closest('[data-media-id]')?.dataset.mediaId;

      if (
        !gallery ||
        !ownMediaId ||
        typeof gallery.updateCarouselImages !== 'function'
      ) {
        return;
      }

      if (key !== 'none') {
        gallery.updateCarouselImages(ownMediaId);
        return;
      }

      const unframedMediaId = this.dataset.unframedMediaId;

      if (
        unframedMediaId &&
        unframedMediaId !== ownMediaId
      ) {
        gallery.updateCarouselImages(unframedMediaId);
        return;
      }

      const containers = Array.from(picker.children).filter((child) =>
        child.classList.contains('product-form__input')
      );

      const selectedOptions = containers.map((container) => {
        const select = container.querySelector('select');

        if (select) return select.value;

        return Array.from(
          container.querySelectorAll('input[type="radio"]')
        ).find((radio) => radio.checked)?.value;
      });

      let variant = picker.currentVariant;

      if (
        !variant &&
        typeof picker.getVariantData === 'function'
      ) {
        variant = picker
          .getVariantData()
          .find((candidate) =>
            candidate.options?.every(
              (option, index) =>
                option === selectedOptions[index]
            )
          );
      }

      const variantMediaId = variant?.featured_media?.id;

      if (
        variantMediaId &&
        String(variantMediaId) !== ownMediaId
      ) {
        gallery.updateCarouselImages(variantMediaId);
        return;
      }

      const mainCarousel = gallery.querySelector(
        '[id^="Main-Carousel"]'
      );

      const fallbackSlide = Array.from(
        mainCarousel?.querySelectorAll(
          '.splide__slide[data-media-id]'
        ) || []
      ).find(
        (slide) => slide.dataset.mediaId !== ownMediaId
      );

      if (fallbackSlide?.dataset.mediaId) {
        gallery.updateCarouselImages(
          fallbackSlide.dataset.mediaId
        );
      }
    }
  }

  customElements.define(
    'wallify-frame-preview',
    WallifyFramePreview
  );
}
